import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';
import { api } from '../App';
import { 
  ArrowLeft, 
  Heart, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Clock,
  Activity,
  Stethoscope,
  RefreshCw,
  Trophy,
  Skull,
  Award,
  TrendingUp,
  Sparkles
} from 'lucide-react';

const ClinicalCaseGame = ({ user, onLogout }) => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  
  const [gameData, setGameData] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showAlternative, setShowAlternative] = useState(false);
  const [gameMessage, setGameMessage] = useState('');
  const [isCorrectAnswer, setIsCorrectAnswer] = useState(null);
  const [gameEnded, setGameEnded] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [rankUp, setRankUp] = useState(null);
  const [showNextButton, setShowNextButton] = useState(false);
  
  useEffect(() => {
    startNewGame();
  }, [caseId]);
  
  const startNewGame = async () => {
    setLoading(true);
    try {
      const response = await api.post('/game/start', null, {
        params: { case_id: caseId }
      });
      
      setGameData(response.data.case);
      setSessionData({
        session_id: response.data.session_id,
        current_step: response.data.current_step,
        steps_taken: response.data.steps_taken
      });
      
      setSelectedAnswer('');
      setShowAlternative(false);
      setGameMessage('');
      setIsCorrectAnswer(null);
      setGameEnded(false);
      setIsSuccessful(false);
      setRankUp(null);
      
    } catch (error) {
      toast.error('Error al iniciar el juego');
      navigate('/escape-room');
    } finally {
      setLoading(false);
    }
  };
  
  const submitAnswerWithOption = async (option) => {
    if (!option || submitting) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await api.post('/game/answer', {
        session_id: sessionData.session_id,
        step_number: sessionData.current_step,
        selected_option: option,
        is_alternative: showAlternative
      });
      
      const result = response.data;
      setGameMessage(result.message);
      setIsCorrectAnswer(result.is_correct);
      
      // Scroll to top to see the message
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      
      // Check for rank up notification
      if (result.rank_up) {
        setRankUp(result.rank_up);
        toast.success(
          `🎉 ¡Ascenso de Rango! Pasaste de ${result.rank_up.old_rank} a ${result.rank_up.new_rank}. ¡Casos exitosos: ${result.rank_up.successful_cases}!`,
          { duration: 5000 }
        );
      }
      
      if (result.game_ended) {
        setGameEnded(true);
        setIsSuccessful(result.is_successful);
        setShowNextButton(false);
      } else {
        // Show next button instead of auto-advancing
        setShowNextButton(true);
        
        // Store next step data for when user clicks button
        if (result.show_alternative) {
          setShowAlternative(true);
          setSessionData(prev => ({ 
            ...prev, 
            steps_taken: prev.steps_taken + 1 
          }));
        } else {
          setSessionData(prev => ({ 
            ...prev, 
            current_step: result.next_step,
            steps_taken: prev.steps_taken + 1
          }));
          setShowAlternative(false);
        }
      }
      
      setSelectedAnswer('');
      
    } catch (error) {
      toast.error('Error al enviar respuesta');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleNextQuestion = () => {
    // Clear feedback message and hide next button
    setGameMessage('');
    setIsCorrectAnswer(null);
    setShowNextButton(false);
  };
  
  const submitAnswer = async () => {
    if (!selectedAnswer || submitting) {
      return;
    }
    await submitAnswerWithOption(selectedAnswer);
  };
  
  const restartGame = () => {
    startNewGame();
  };

  const goBack = () => {
    navigate('/escape-room');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando caso clínico...</p>
        </div>
      </div>
    );
  }
  
  if (!gameData || !sessionData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-black mb-2">Error al cargar el caso</h2>
          <Button onClick={goBack} className="bg-black hover:bg-gray-800 text-white">
            Volver al Escape Room
          </Button>
        </div>
      </div>
    );
  }
  
  const currentStepData = gameData.steps[sessionData.current_step - 1];
  const progressPercentage = (sessionData.current_step / gameData.steps.length) * 100;
  
  // Game ended screen
  if (gameEnded) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full shadow-2xl border-2 border-black">
          <CardHeader className={`text-white ${isSuccessful ? 'bg-black' : 'bg-gray-500'}`}>
            <CardTitle className="text-center text-3xl flex items-center justify-center">
              {isSuccessful ? (
                <>
                  <Trophy className="mr-3 h-10 w-10" />
                  ¡Caso Completado!
                </>
              ) : (
                <>
                  <Skull className="mr-3 h-10 w-10" />
                  Caso Terminado
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-8 pb-6">
            <div className="text-center mb-6">
              {isSuccessful ? (
                <CheckCircle className="h-24 w-24 text-black mx-auto mb-4" />
              ) : (
                <XCircle className="h-24 w-24 text-gray-400 mx-auto mb-4" />
              )}
              
              <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 mb-6">
                <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-line">
                  {gameMessage}
                </p>
              </div>

              {rankUp && (
                <Alert className="mb-6 bg-gray-50 border-2 border-black">
                  <Sparkles className="h-5 w-5 text-black" />
                  <AlertDescription className="text-black font-medium">
                    🎉 ¡Ascenso de Rango! Has pasado de <strong>{rankUp.old_rank}</strong> a <strong>{rankUp.new_rank}</strong>
                    <br />
                    Casos exitosos: {rankUp.successful_cases}
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-black">{sessionData.steps_taken}</div>
                  <div className="text-sm text-gray-600">Pasos Tomados</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-black">{gameData.steps.length}</div>
                  <div className="text-sm text-gray-600">Pasos del Caso</div>
                </div>
              </div>
            </div>
            
            <div className="flex gap-4">
              <Button
                onClick={restartGame}
                className="flex-1 bg-black hover:bg-gray-800 text-white"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Intentar de Nuevo
              </Button>
              <Button
                onClick={goBack}
                variant="outline"
                className="flex-1 border-black text-black hover:bg-gray-100"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Volver al Escape Room
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Active game screen
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-black text-white shadow-lg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Button
              variant="ghost"
              onClick={goBack}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Salir
            </Button>
            <div className="flex items-center space-x-4">
              <div className="flex items-center bg-white/20 px-4 py-2 rounded-full">
                <Heart className="h-5 w-5 mr-2" />
                <span className="font-semibold">Paso {sessionData.current_step}/{gameData.steps.length}</span>
              </div>
              <div className="flex items-center bg-white/20 px-4 py-2 rounded-full">
                <Activity className="h-5 w-5 mr-2" />
                <span className="font-semibold">{sessionData.steps_taken} movimientos</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Case Info */}
        <Card className="mb-6 border-2 border-black shadow-xl">
          <CardHeader className="bg-black text-white">
            <CardTitle className="flex items-center">
              <Stethoscope className="mr-3 h-6 w-6" />
              {gameData.title}
            </CardTitle>
            <div className="flex items-center mt-2">
              <Badge variant="secondary" className="bg-white/20 text-white">
                {gameData.theme}
              </Badge>
            </div>
          </CardHeader>
        </Card>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-black">Progreso del Caso</span>
            <span className="text-sm text-gray-600">{Math.round(progressPercentage)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-black h-3 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>

        {/* Message Display */}
        {gameMessage && (
          <Alert className={`mb-6 border-4 shadow-2xl ${
            isCorrectAnswer
              ? 'bg-gray-50 border-black'
              : 'bg-gray-50 border-gray-400'
          }`}>
            <div className="flex items-start space-x-4 p-2">
              <img 
                src={
                  isCorrectAnswer
                    ? 'https://images.unsplash.com/photo-1618498082410-b4aa22193b38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGRvY3RvcnxlbnwwfHx8fDE3NjExNDk1NjR8MA&ixlib=rb-4.1.0&q=85&w=200'
                    : 'https://images.pexels.com/photos/3771115/pexels-photo-3771115.jpeg?w=200'
                }
                alt={isCorrectAnswer ? 'Doctor feliz - Respuesta correcta' : 'Doctor preocupado - Respuesta incorrecta'}
                className="w-28 h-28 object-cover rounded-full border-4 border-white shadow-2xl flex-shrink-0"
                style={{
                  objectPosition: 'center'
                }}
              />
              <div className="flex-1">
                <div className={`flex items-center mb-2 ${
                  isCorrectAnswer ? 'text-black' : 'text-gray-500'
                }`}>
                  {isCorrectAnswer ? (
                    <CheckCircle className="h-6 w-6 mr-2" />
                  ) : (
                    <XCircle className="h-6 w-6 mr-2" />
                  )}
                  <span className="font-bold text-lg">
                    {isCorrectAnswer ? '¡Respuesta Correcta!' : 'Respuesta Incorrecta'}
                  </span>
                </div>
                <AlertDescription className={`text-lg font-semibold leading-relaxed ${
                  isCorrectAnswer ? 'text-black' : 'text-gray-600'
                }`}>
                  {gameMessage}
                </AlertDescription>
                
                {/* Next Button - Only show when feedback is displayed and game hasn't ended */}
                {showNextButton && !gameEnded && (
                  <div className="mt-4">
                    <Button 
                      onClick={handleNextQuestion}
                      className="w-full py-3 font-bold text-lg shadow-lg bg-black hover:bg-gray-800 text-white"
                    >
                      Continuar con el caso clínico →
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Alert>
        )}

        {/* Question Card */}
        <Card className="shadow-xl border-2 border-gray-200">
          <CardHeader className="bg-gray-50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl text-black">
                {showAlternative ? '🔄 Pregunta Alternativa' : `📋 Paso ${sessionData.current_step}`}
              </CardTitle>
              {showAlternative && (
                <Badge variant="destructive" className="animate-pulse bg-gray-600">
                  ¡Recuperación!
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-black text-lg leading-relaxed whitespace-pre-line">
                {showAlternative ? currentStepData.Pregunta_Alternativa : currentStepData.Pregunta_Principal}
              </p>
            </div>

            {/* Answer Options */}
            <div className="space-y-3">
              {['A', 'B', 'C', 'D'].map((option) => {
                const optionKey = showAlternative ? `${option}_Alternativa` : `${option}_Principal`;
                const optionText = currentStepData[optionKey];
                
                if (!optionText) return null;
                
                return (
                  <button
                    key={option}
                    onClick={() => {
                      if (!submitting && !gameMessage) {
                        setSelectedAnswer(option);
                        // Auto-submit after selection
                        setTimeout(() => {
                          submitAnswerWithOption(option);
                        }, 300);
                      }
                    }}
                    disabled={submitting || gameMessage}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                      selectedAnswer === option
                        ? 'border-black bg-gray-50 shadow-md transform scale-[1.02]'
                        : 'border-gray-300 hover:border-black hover:bg-gray-50'
                    } ${(submitting || gameMessage) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-start">
                      <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold mr-3 ${
                        selectedAnswer === option
                          ? 'bg-black text-white'
                          : 'bg-gray-200 text-black'
                      }`}>
                        {option}
                      </span>
                      <span className="text-black leading-relaxed">{optionText}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Loading indicator when submitting */}
            {submitting && (
              <div className="mt-6 text-center">
                <Clock className="h-8 w-8 animate-spin text-black mx-auto mb-2" />
                <p className="text-gray-600">Procesando tu respuesta...</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClinicalCaseGame;
